import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { Link } from '@inertiajs/react';
import { User, DeliveryCheckResult } from '../../types/landing';
import { useDeliveryZoneCheck } from '../../hooks/useDeliveryZoneCheck';
import type { AddressType, GeocodedAddress } from '../../types/address';
import { useGeocoding } from '../../hooks/useGeocoding';
import { useGps } from '../../hooks/useGps';
import { route } from '../../utils/route';

import { AddressTypeSelector } from './AddressTypeSelector';
import { StreetInput } from './StreetInput';
import { NeighborhoodSelect } from './NeighborhoodSelect';
import { GpsButton } from './GpsButton';

const MapPreview = React.lazy(() =>
  import('../address/MapPreview').then(m => ({ default: m.MapPreview }))
);

interface DeliveryCheckModalProps {
  user: User | null;
  onClose: () => void;
  onResult: (result: DeliveryCheckResult | null) => void;
  result: DeliveryCheckResult | null;
  showMap?: boolean;
}

function getDisplayName(location: GeocodedAddress, data: { block?: string; house?: string; isCountry?: boolean }) {
  if (data.isCountry) {
    const city = location.address?.city || 'Cordoba';
    const neighborhoodName = location.display_name;
    if (!data.block || !data.house) return `${neighborhoodName}, ${city}, Argentina`;
    return `${neighborhoodName} Mzna. ${data.block} Lote ${data.house}, ${city}, Argentina`;
  }
  const road = location.address?.road || location.address?.pedestrian || '';
  const number = location.address?.house_number || 'S/N';
  const city = location.address?.city || 'Cordoba';
  return `${road} ${number}, ${city}, Argentina`;
}

export function DeliveryCheckModal({ user, onClose, onResult, result, showMap = true }: DeliveryCheckModalProps) {
  const [addressType, setAddressType] = useState<AddressType>('street');
  const [streetAddress, setStreetAddress] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [block, setBlock] = useState('');
  const [house, setHouse] = useState('');
  const [geocodedAddress, setGeocodedAddress] = useState<GeocodedAddress | null>(null);
  const [streetSuggestions, setStreetSuggestions] = useState<GeocodedAddress[]>([]);
  const [neighborhoodSuggestions, setNeighborhoodSuggestions] = useState<GeocodedAddress[]>([]);
  const [guestAddressSaved, setGuestAddressSaved] = useState(false);
  const [fromGps, setFromGps] = useState(false);

  const { searchAddress, searchNeighborhood, reverseGeocode, isLoading: isGeocodingLoading, error: geocodingError } = useGeocoding({ debounceMs: 300 });
  const { getCurrentPosition, isLoading: isGpsLoading, error: gpsError } = useGps();
  const { checkDeliveryZone, saveGuestAddress, isLoading: isCheckingZone } = useDeliveryZoneCheck();

  const isCountry = addressType === 'country';

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    if (!isCountry && streetAddress.length >= 3 && !geocodedAddress) {
      searchAddress(streetAddress).then(setStreetSuggestions);
    } else {
      setStreetSuggestions([]);
    }
  }, [streetAddress, isCountry, searchAddress, geocodedAddress]);

  useEffect(() => {
    if (isCountry && neighborhood.length >= 2 && !geocodedAddress) {
      searchNeighborhood(neighborhood).then(setNeighborhoodSuggestions);
    } else {
      setNeighborhoodSuggestions([]);
    }
  }, [neighborhood, isCountry, searchNeighborhood, geocodedAddress]);

  const checkZone = useCallback(async (address: GeocodedAddress, addressBlock?: string, addressHouse?: string) => {
    const checkResult = await checkDeliveryZone(address.lat, address.lon);
    onResult(checkResult);
    if (checkResult.isDeliverable && !user) {
      const displayName = getDisplayName(address, { block: addressBlock, house: addressHouse, isCountry: addressType === 'country' });
      try {
        await saveGuestAddress({
          lat: address.lat, lng: address.lon, address: displayName,
          is_country: addressType === 'country',
          neighborhood: addressType === 'country' ? neighborhood : undefined,
          block: addressBlock, house: addressHouse,
        });
        setGuestAddressSaved(true);
      } catch (_) {}
    }
  }, [checkDeliveryZone, saveGuestAddress, onResult, user, addressType, neighborhood]);

  const handleStreetAddressSelect = useCallback((address: GeocodedAddress | null) => {
    if (address?.is_private_neighborhood && address.parsed_country_data) {
      setAddressType('country');
      setNeighborhood(address.parsed_country_data.neighborhood);
      setBlock(address.parsed_country_data.block || '');
      setHouse(address.parsed_country_data.house || '');
      setStreetAddress('');
      setGeocodedAddress(address);
      setStreetSuggestions([]);
      setNeighborhoodSuggestions([]);
      if (address.parsed_country_data.block && address.parsed_country_data.house) {
        checkZone(address, address.parsed_country_data.block, address.parsed_country_data.house);
      }
    } else {
      setGeocodedAddress(address);
      if (address) {
        setStreetAddress(address.display_name);
        checkZone(address);
      }
      setStreetSuggestions([]);
    }
  }, [checkZone]);

  const handleNeighborhoodSelect = useCallback((address: GeocodedAddress | null) => {
    setGeocodedAddress(address);
    if (address) setNeighborhood(address.display_name);
    setNeighborhoodSuggestions([]);
  }, []);

  const handleUseGps = async () => {
    try {
      const position = await getCurrentPosition();
      const gpsResult = await reverseGeocode(position.latitude, position.longitude);
      if (gpsResult) {
        setFromGps(true);
        if (gpsResult.private_neighborhood) {
          setAddressType('country');
          const nhAddress = { ...gpsResult.private_neighborhood, lat: position.latitude, lon: position.longitude };
          setGeocodedAddress(nhAddress);
          setNeighborhood(gpsResult.private_neighborhood.display_name);
          checkZone(nhAddress);
        } else {
          setAddressType('street');
          setGeocodedAddress(gpsResult);
          setStreetAddress(gpsResult.display_name);
          checkZone(gpsResult);
        }
      }
    } catch (_) {}
  };

  const handleTypeChange = (type: AddressType) => {
    setAddressType(type);
    setStreetAddress(''); setNeighborhood(''); setBlock(''); setHouse('');
    setGeocodedAddress(null); setStreetSuggestions([]); setNeighborhoodSuggestions([]);
    onResult(null); setGuestAddressSaved(false); setFromGps(false);
  };

  const handleVerifyCountry = () => {
    if (geocodedAddress && block && house) checkZone(geocodedAddress, block, house);
  };

  const handleReset = () => {
    setStreetAddress(''); setNeighborhood(''); setBlock(''); setHouse('');
    setGeocodedAddress(null); setStreetSuggestions([]); setNeighborhoodSuggestions([]);
    onResult(null); setGuestAddressSaved(false); setFromGps(false);
  };

  const canVerifyCountry = isCountry && geocodedAddress && block && house;
  const showResult = result && !isCheckingZone;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center z-[10000] p-5 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`bg-white rounded-2xl max-w-[700px] w-full my-auto shadow-2xl animate-modal-slide-in flex flex-col max-h-[calc(100vh-40px)] ${streetAddress !== '' ? 'min-h-[680px]' : 'min-h-[500px]'} max-sm:max-h-[95vh] max-sm:mx-2.5`}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-800 m-0 flex items-center gap-2.5">
            <i className="fa fa-truck text-green-500"></i>
            Verificar zona de entrega
          </h3>
          <button className="bg-transparent border-none text-3xl text-gray-400 cursor-pointer px-2 py-1 leading-none hover:text-gray-800" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto min-h-0 max-sm:p-4">
          <GpsButton onClick={handleUseGps} isLoading={isGpsLoading} error={gpsError} disabled={isCheckingZone} />

          <div className="flex flex-col gap-5">
            <AddressTypeSelector value={addressType} onChange={handleTypeChange} disabled={isCheckingZone} />

            {!isCountry && (
              <StreetInput
                value={streetAddress} onChange={setStreetAddress}
                onAddressSelect={handleStreetAddressSelect} selectedAddress={geocodedAddress}
                suggestions={streetSuggestions} isLoading={isGeocodingLoading}
                error={geocodingError} disabled={isCheckingZone}
              />
            )}

            {isCountry && (
              <>
                <NeighborhoodSelect
                  value={neighborhood} onChange={(v) => { setNeighborhood(v); setFromGps(false); }}
                  onSelect={handleNeighborhoodSelect} selectedNeighborhood={geocodedAddress}
                  suggestions={neighborhoodSuggestions} isLoading={isGeocodingLoading}
                  error={geocodingError} disabled={isCheckingZone}
                />

                {!fromGps && (
                  <>
                    <div className="flex gap-4 max-sm:flex-col max-sm:gap-0">
                      <div className="flex-1">
                        <label className="block text-[15px] font-semibold mb-2">Manzana</label>
                        <input
                          type="text" value={block} onChange={(e) => setBlock(e.target.value)}
                          disabled={isCheckingZone} maxLength={12} autoComplete="off"
                          className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                          placeholder="Ej: 5"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-[15px] font-semibold mb-2">Lote</label>
                        <input
                          type="text" value={house} onChange={(e) => setHouse(e.target.value)}
                          disabled={isCheckingZone} maxLength={12} autoComplete="off"
                          className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                          placeholder="Ej: 12"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleVerifyCountry}
                      disabled={!canVerifyCountry || isCheckingZone}
                      className={`w-full py-3.5 px-6 text-base font-semibold rounded-lg transition-all ${
                        canVerifyCountry && !isCheckingZone
                          ? 'text-white bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 cursor-pointer'
                          : 'bg-gray-300 text-white cursor-not-allowed'
                      }`}
                    >
                      {isCheckingZone ? 'Verificando...' : 'Verificar zona'}
                    </button>
                  </>
                )}
              </>
            )}

            {showMap && geocodedAddress && (
              <div className="h-[200px] rounded-lg overflow-hidden mt-4">
                <React.Suspense fallback={<div className="h-full bg-gray-100" />}>
                  <MapPreview latitude={geocodedAddress.lat} longitude={geocodedAddress.lon} height="100%" />
                </React.Suspense>
              </div>
            )}
          </div>

          {/* Loading */}
          {isCheckingZone && (
            <div className="flex items-center justify-center gap-3 py-8 text-gray-500">
              <div className="w-7 h-7 border-[3px] border-gray-200 border-t-green-500 rounded-full animate-spin"></div>
              <span>Verificando zona de entrega...</span>
            </div>
          )}

          {/* Result */}
          {showResult && (
            <div className={`mt-6 p-5 rounded-xl animate-fade-in border-2 ${
              result.isDeliverable
                ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-500'
                : 'bg-gradient-to-br from-red-50 to-red-100 border-red-500'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`text-3xl ${result.isDeliverable ? 'text-green-500' : 'text-red-500'}`}>
                  <i className={`fa ${result.isDeliverable ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                </div>
                <h4 className={`text-lg font-bold m-0 ${result.isDeliverable ? 'text-green-800' : 'text-red-800'}`}>
                  {result.isDeliverable ? '¡Hacemos entregas en tu zona!' : 'No llegamos a esta zona'}
                </h4>
              </div>
              <div className="text-[15px] text-gray-600 mb-4">{result.message}</div>

              {result.isDeliverable && result.dates.length > 0 && (
                <div className="bg-white rounded-lg p-3 mb-4">
                  {result.dates.slice(0, 3).map((date, idx) => (
                    <div key={date.value} className={`flex items-center gap-2.5 py-2.5 ${idx !== Math.min(2, result.dates.length - 1) ? 'border-b border-gray-100' : ''}`}>
                      {idx === 0 ? <i className="fa fa-star text-amber-400"></i> : <i className="fa fa-calendar-o text-gray-400"></i>}
                      <span className="flex-1 font-medium text-gray-800">{date.label}</span>
                      <span className="text-[13px] text-gray-500 bg-gray-100 px-2 py-1 rounded">{date.timeRange}</span>
                    </div>
                  ))}
                </div>
              )}

              {result.isDeliverable && result.shipping_tiers && result.shipping_tiers.length > 0 ? (
                <div className="bg-white rounded-lg p-3 mb-4">
                  <div className="text-xs text-gray-500 mb-2 font-medium">Costo de envío según tu compra</div>
                  <div className="flex flex-wrap gap-2">
                    {[...result.shipping_tiers].sort((a, b) => a.min_subtotal - b.min_subtotal).map((tier, i) => (
                      <div key={tier.min_subtotal} className={`flex-1 min-w-[80px] text-center rounded-lg py-2 px-2 ${
                        tier.shipping_cost === 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                      }`}>
                        <div className={`text-[13px] font-bold ${tier.shipping_cost === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                          {tier.shipping_cost === 0 ? 'Gratis' : `$${tier.shipping_cost.toLocaleString('es-AR')}`}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {i === 0 ? `Desde $${tier.min_subtotal.toLocaleString('es-AR')}` : `+$${tier.min_subtotal.toLocaleString('es-AR')}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : result.isDeliverable && (result.shipping_cost != null || result.minimum_order != null) ? (
                <div className="flex gap-3 mb-4 max-sm:flex-col">
                  {result.shipping_cost != null && (
                    <div className="flex-1 bg-white rounded-lg p-3 flex items-center gap-2.5">
                      <i className="fa fa-truck text-green-500 text-lg"></i>
                      <div>
                        <div className="text-xs text-gray-500">Costo de envio</div>
                        <div className="text-base font-bold text-gray-800">${result.shipping_cost.toLocaleString('es-AR')}</div>
                      </div>
                    </div>
                  )}
                  {result.minimum_order != null && (
                    <div className="flex-1 bg-white rounded-lg p-3 flex items-center gap-2.5">
                      <i className="fa fa-shopping-basket text-amber-500 text-lg"></i>
                      <div>
                        <div className="text-xs text-gray-500">Pedido minimo</div>
                        <div className="text-base font-bold text-gray-800">${result.minimum_order.toLocaleString('es-AR')}</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {result.isDeliverable && (
                <>
                  <Link
                    href={route('products.index')}
                    className="block w-full py-3.5 text-base font-semibold text-white bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg text-center no-underline transition-all"
                  >
                    <i className="fa fa-shopping-cart mr-2"></i>
                    Empezar a comprar
                  </Link>
                  {!user && guestAddressSaved && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                      <i className="fa fa-info-circle mr-2"></i>
                      Tu dirección se guardó. Al iniciar sesión, se agregará automáticamente a tu cuenta.
                    </div>
                  )}
                </>
              )}

              {!result.isDeliverable && (
                <button
                  onClick={handleReset}
                  className="block w-full py-3.5 text-base font-semibold text-gray-800 bg-gray-100 border-2 border-gray-300 hover:bg-gray-200 rounded-lg text-center transition-all mt-2.5 cursor-pointer"
                >
                  <i className="fa fa-refresh mr-2"></i>
                  Probar otra dirección
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
